/**
 * How "in use / free" is drawn over anything on the plan (a room, a phone booth, later a desk):
 *
 * Stripes are the "in use" message itself, so they only ever appear while something is occupied:
 *
 * - free (no occupant): a solid, plain fill — the occupancy green, or the thing's own colour when it
 *   has one (a room's booking or lease status: orange, blue, teal ...); never any stripes;
 * - busy (occupant present): diagonal stripes of the thing's own colour alternating with occupancy
 *   red when it has one, or with nothing (the plan shows through) when it has none, i.e. its colour
 *   is the neutral grey or already a red / green itself.
 *
 * The base colour is never replaced: a room with a predefined colour stays that colour, only
 * striped once an occupant shows up.
 */

/** A room with nothing to say about itself is drawn in this grey (`getRoomColor`'s fallback). */
export const NEUTRAL_COLOR = "#64748b";
const IN_USE = "#dc2626";
const FREE = "#16a34a";

export function occupancyColor(occupied: boolean): string {
  return occupied ? IN_USE : FREE;
}

/**
 * The fill a free thing shows: solid green, unless it has a colour of its own to keep.
 * Unlike `hasOwnColor`, a red or green base IS kept here — free never repaints the base colour;
 * `hasOwnColor`'s red/green exclusion is only about not striping red on red.
 */
export function freeColor(base: string | null | undefined): string {
  if (base && base.toLowerCase() !== NEUTRAL_COLOR) return base;
  return FREE;
}

/**
 * The fill of an occupied thing: `url(#id)` into an `OccupancyStripes` pattern under that id.
 * Pair with the guarded `<defs>` (`{occupied && <defs>...}`) so free things emit no pattern.
 */
export function occupiedFill(id: string): string {
  return `url(#${id})`;
}

/** Whether `base` is a colour of its own worth keeping next to the occupancy colour. */
export function hasOwnColor(base: string | null | undefined, occupied: boolean): base is string {
  if (!base) return false;
  const normalised = base.toLowerCase();
  if (normalised === NEUTRAL_COLOR) return false;
  // Already red / green: striping red on red says nothing.
  const reds = ["#ef4444", "#dc2626"];
  const greens = ["#22c55e", "#16a34a"];
  return !(occupied ? reds : greens).includes(normalised);
}

export function stripedPatternId(key: string): string {
  return `nodus-fp-occ-${key}`;
}

/**
 * The `<pattern>` to put in the SVG next to the shape; fill the shape with `occupiedFill(id)`.
 * Only meaningful while occupied — a free thing is drawn solid (`freeColor`), not striped.
 */
export function OccupancyStripes({ id, base, stripe = 6 }: { id: string; base?: string | null; stripe?: number }) {
  return (
    <pattern id={id} width={stripe * 2} height={stripe * 2} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
      {hasOwnColor(base, true) && <rect width={stripe} height={stripe * 2} fill={base} />}
      <rect x={stripe} width={stripe} height={stripe * 2} fill={IN_USE} />
    </pattern>
  );
}
